import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  AccountBalanceWallet,
  Article,
  Close,
  CreditCard,
  Edit,
  InsertDriveFile,
  LocalAtm,
  ReceiptLong,
  UploadFile,
  Visibility,
} from '@mui/icons-material';
import MainText from '../../components/MainText';
import DatePick from '../../components/DatePick';
import API, { PaymentCrud, SettlementsAPI } from '../../services/api';
import PaymentActivityTimeline from './components/PaymentActivityTimeline';
import PaymentAdjustmentsSection from './components/PaymentAdjustmentsSection';
import PaymentAttachmentPreview from './components/PaymentAttachmentPreview';
import PaymentNotesSection from './components/PaymentNotesSection';
import PaymentProviderReceiptSection from './components/PaymentProviderReceiptSection';
import PaymentRelatedDocumentsSection from './components/PaymentRelatedDocumentsSection';
import PaymentStatusChip from './components/PaymentStatusChip';
import {
  formatCurrency,
  formatDate,
  isCashMethod,
  methodRequiresAttachment,
  normalizePaymentDetailData,
  toNumber,
} from './paymentNormalizer';

const BTN_SX = {
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 1,
  height: 30,
  px: 1.25,
};

const emptyValue = 'No disponible';

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const getAttachmentUrl = (attachment) => (
  attachment?.url || attachment?.file || attachment?.file_url || attachment?.raw?.file || ''
);

const getAttachmentKind = (attachment) => {
  const raw = String(attachment?.kind || attachment?.type || '').toLowerCase();
  if (raw.includes('image')) return 'image';
  if (raw.includes('pdf')) return 'pdf';
  const url = String(getAttachmentUrl(attachment)).split('?')[0].toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(url)) return 'image';
  if (/\.pdf$/.test(url)) return 'pdf';
  return 'file';
};

const getPrimaryPaymentDocumentId = (attachment) => {
  if (!attachment?.primary) return null;
  const raw = String(attachment.id || '');
  const id = raw.includes(':') ? raw.split(':')[0] : raw;
  return /^\d+$/.test(id) ? id : null;
};

const getAttachmentRecordId = (attachment) => {
  const id = attachment?.raw?.id ?? attachment?.id;
  const text = String(id ?? '').trim();
  return /^\d+$/.test(text) ? text : null;
};

function SectionCard({ children, sx }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 1,
        boxShadow: 'none',
        borderColor: 'divider',
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function DetailField({ label, value, strong = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: strong ? 600 : 400,
          fontVariantNumeric: strong ? 'tabular-nums' : 'normal',
          overflowWrap: 'anywhere',
          color: value ? 'text.primary' : 'text.secondary',
        }}
      >
        {value || emptyValue}
      </Typography>
    </Box>
  );
}

const METHOD_STYLE = {
  ach: { Icon: AccountBalance, color: 'info.main' },
  transfer: { Icon: AccountBalance, color: 'primary.main' },
  check: { Icon: Article, color: 'secondary.main' },
  cash: { Icon: LocalAtm, color: 'success.main' },
  card: { Icon: CreditCard, color: 'warning.main' },
  other: { Icon: AccountBalanceWallet, color: 'text.secondary' },
};

function PaymentMethodRow({
  method,
  onEdit,
  onPreviewPaymentAttachment,
  onDownloadPaymentAttachment,
  onReplacePaymentAttachment,
  onDeletePaymentAttachment,
  onAttachPaymentAttachment,
}) {
  const meta = METHOD_STYLE[method?.type] || METHOD_STYLE.other;
  const Icon = meta.Icon;
  const attachments = Array.isArray(method?.attachments) ? method.attachments : [];
  const receiptText = attachments.length > 0
    ? `${attachments.length} archivo${attachments.length === 1 ? '' : 's'}`
    : isCashMethod(method)
      ? 'No aplica'
      : methodRequiresAttachment(method)
        ? 'Pendiente'
        : 'Sin comprobante';

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.9, bgcolor: 'action.hover' }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'background.paper',
            color: meta.color,
            border: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
              {method?.label || 'Metodo'}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={method?.isActive === false ? 'Anulado' : 'Activo'}
              color={method?.isActive === false ? 'error' : 'success'}
              sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[method?.reference || (isCashMethod(method) ? 'Sin referencia' : ''), method?.bank?.label].filter(Boolean).join(' - ')}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(method?.amount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {receiptText}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          aria-label="Editar medio de pago"
          onClick={() => onEdit?.(method)}
          disabled={method?.isActive === false}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ p: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1,
            mb: attachments.length ? 1 : 0,
          }}
        >
          <DetailField label="Referencia" value={method?.reference || (isCashMethod(method) ? 'No aplica' : '')} />
          <DetailField label="Banco / cuenta" value={method?.bank?.label} />
          <DetailField label="Fecha" value={method?.date ? formatDate(method.date) : ''} />
        </Box>

        {attachments.length > 0 ? (
          <Stack spacing={0.75}>
            {attachments.map((attachment) => (
              <PaymentAttachmentPreview
                key={attachment.id || attachment.url || attachment.name}
                attachment={attachment}
                onView={onPreviewPaymentAttachment}
                onDownload={onDownloadPaymentAttachment}
                onReplace={onReplacePaymentAttachment}
                onDelete={onDeletePaymentAttachment}
              />
            ))}
          </Stack>
        ) : (
          <Alert
            severity={methodRequiresAttachment(method) ? 'warning' : 'info'}
            sx={{ py: 0.25 }}
            action={methodRequiresAttachment(method) ? (
              <Button
                size="small"
                color="inherit"
                startIcon={<UploadFile fontSize="small" />}
                onClick={() => onAttachPaymentAttachment?.(method)}
              >
                Adjuntar
              </Button>
            ) : null}
          >
            {isCashMethod(method)
              ? 'Este método no requiere comprobante.'
              : methodRequiresAttachment(method)
                ? 'Comprobante pendiente. Adjunta o reemplaza el archivo desde este detalle.'
                : 'Sin comprobante adjunto.'}
          </Alert>
        )}
      </Box>
    </Box>
  );
}

const toLocalDateInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function PaymentMethodEditDialog({ method, onClose, onSaved }) {
  const open = Boolean(method);
  const [form, setForm] = useState({
    paymentMethod: 'cash',
    payment_date: '',
    reference: '',
    bank_account_id: '',
    comment: '',
    correction_reason: '',
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      paymentMethod: method?.type || method?.rawType || 'cash',
      payment_date: toLocalDateInput(method?.date),
      reference: method?.reference || '',
      bank_account_id: String(method?.bank?.id || method?.bank?.raw?.id || ''),
      comment: method?.raw?.comment || '',
      correction_reason: '',
    });
    setError('');
    if (Array.isArray(method?.raw?.available_bank_accounts)) {
      setBankAccounts(method.raw.available_bank_accounts);
      setLoadingBanks(false);
      return;
    }
    setLoadingBanks(true);
    const supplierId = method?.raw?.supplier_id;
    API.get('bank-account/')
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (data?.results || []);
        setBankAccounts(
          supplierId
            ? rows.filter((account) => String(account.supplier || account.supplier_id) === String(supplierId))
            : rows
        );
      })
      .catch(() => setBankAccounts([]))
      .finally(() => setLoadingBanks(false));
  }, [open, method]);

  const requiresBank = ['transfer', 'ach', 'check'].includes(form.paymentMethod);
  const requiresReference = ['transfer', 'check'].includes(form.paymentMethod);

  const handleSubmit = async () => {
    if (!form.payment_date) {
      setError('La fecha de pago es requerida.');
      return;
    }
    if (form.payment_date > todayInputValue()) {
      setError('La fecha de pago no puede ser futura.');
      return;
    }
    if (!form.correction_reason.trim()) {
      setError('El motivo de corrección es requerido.');
      return;
    }
    if (requiresReference && !form.reference.trim()) {
      setError('Transferencia y cheque requieren referencia bancaria.');
      return;
    }
    if (requiresBank && !form.bank_account_id) {
      setError('Transferencia, ACH y cheque requieren cuenta bancaria.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await SettlementsAPI.updatePayment(method.id, {
        ...form,
        bank_account_id: form.bank_account_id || null,
      });
      await onSaved?.();
      onClose();
    } catch (requestError) {
      const data = requestError?.response?.data;
      setError(
        data?.detail
        || data?.reference?.[0]
        || data?.bank_account_id?.[0]
        || data?.payment_date?.[0]
        || data?.correction_reason?.[0]
        || 'No se pudo corregir el medio de pago.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Editar medio de pago</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Paper variant="outlined" sx={{ p: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Monto bloqueado</Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatCurrency(method?.amount || 0)}
            </Typography>
          </Paper>
          <FormControl size="small" fullWidth>
            <InputLabel>Método</InputLabel>
            <Select
              label="Método"
              value={form.paymentMethod}
              onChange={(event) => setForm((current) => ({
                ...current,
                paymentMethod: event.target.value,
                ...(event.target.value === 'cash' ? { bank_account_id: '', reference: '' } : {}),
              }))}
            >
              <MenuItem value="transfer">Transferencia</MenuItem>
              <MenuItem value="ach">Transferencia ACH</MenuItem>
              <MenuItem value="check">Cheque</MenuItem>
              <MenuItem value="cash">Efectivo</MenuItem>
              <MenuItem value="abasto">Abasto</MenuItem>
            </Select>
          </FormControl>
          <DatePick
            name="payment_date"
            label="Fecha de pago"
            value={form.payment_date}
            onChange={(date) => setForm((current) => ({
              ...current,
              payment_date: toLocalDateInput(date),
            }))}
            size="small"
            allowHistoricalDates
            allowFutureDates={false}
            width="100%"
            maxWidth={700}
          />
          <TextField
            size="small"
            fullWidth
            label={form.paymentMethod === 'cash' ? 'Referencia interna automática' : 'Referencia'}
            value={form.paymentMethod === 'cash' ? 'Se generará al guardar' : form.reference}
            disabled={form.paymentMethod === 'cash'}
            onChange={(event) => setForm((current) => ({ ...current, reference: event.target.value }))}
            required={requiresReference}
            helperText={
              form.paymentMethod === 'ach'
                ? 'La referencia es opcional para ACH; el comprobante debe adjuntarse al pago.'
                : ''
            }
          />
          <FormControl size="small" fullWidth disabled={!requiresBank || loadingBanks}>
            <InputLabel>Cuenta bancaria</InputLabel>
            <Select
              label="Cuenta bancaria"
              value={form.bank_account_id}
              onChange={(event) => setForm((current) => ({
                ...current,
                bank_account_id: event.target.value,
              }))}
            >
              <MenuItem value=""><em>Sin cuenta</em></MenuItem>
              {bankAccounts.map((account) => (
                <MenuItem key={account.id} value={String(account.id)}>
                  {account.bankName} · {account.accNumber} {account.accType ? `(${account.accType})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="Comentario"
            value={form.comment}
            onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
          />
          <TextField
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            label="Motivo de corrección"
            value={form.correction_reason}
            onChange={(event) => setForm((current) => ({
              ...current,
              correction_reason: event.target.value,
            }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar corrección'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AttachmentPreviewDialog({ open, attachment, onClose }) {
  const url = getAttachmentUrl(attachment);
  const kind = getAttachmentKind(attachment);
  const name = attachment?.name || 'Archivo';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
          {name}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {url && (
            <Button
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
              size="small"
              variant="contained"
              sx={{ ...BTN_SX, bgcolor: 'rgba(255,255,255,0.16)', boxShadow: 'none' }}
            >
              Abrir
            </Button>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent
        dividers
        sx={{
          p: 0,
          height: { xs: 460, md: 680 },
          display: 'grid',
          placeItems: 'center',
          bgcolor: kind === 'image' ? '#111827' : 'background.default',
        }}
      >
        {kind === 'image' && url ? (
          <Box component="img" src={url} alt={name} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        ) : kind === 'pdf' && url ? (
          <Box component="object" data={url} type="application/pdf" sx={{ width: '100%', height: '100%' }}>
            <Box component="iframe" title={name} src={url} sx={{ width: '100%', height: '100%', border: 0 }} />
          </Box>
        ) : (
          <Stack alignItems="center" spacing={1}>
            <InsertDriveFile sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Vista previa no disponible.
            </Typography>
            {url && (
              <Button component="a" href={url} target="_blank" rel="noreferrer" size="small" variant="outlined" sx={BTN_SX}>
                Abrir o descargar
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

const getInvoiceItems = (invoiceData, fallbackDoc) => {
  const source = invoiceData || fallbackDoc?.raw || fallbackDoc || {};
  const candidates = [
    source.invoice_detail,
    source.invoice_details,
    source.details,
    source.items,
    source.products,
  ];
  return candidates.find((value) => Array.isArray(value)) || [];
};

function InvoiceDetailDialog({ open, doc, invoiceData, loading, error, onClose }) {
  const source = invoiceData || doc?.raw || doc || {};
  const items = getInvoiceItems(invoiceData, doc);
  const code = source.code || [source.number || doc?.number, source.series || doc?.series].filter(Boolean).join(' - ') || doc?.code;
  const provider = source.supplier?.companyName || source.supplier?.legalName || source.supplier || doc?.supplier;
  const receptionDate = source.reception_date || source.received_at || source.createdAt || doc?.date;
  const issueDate = source.date || source.issue_date || doc?.date;
  const total = source.total_effective ?? source.total ?? doc?.total ?? doc?.amount;
  const balance = source.balance ?? source.invoice_balance ?? doc?.balance;
  const status = source.invoiceStatus || source.status || source.paymentStatus || doc?.status;
  const notes = source.notes || source.comment || source.observation || source.observations || '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ py: 1.25, pr: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
              Factura {code || emptyValue}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {provider || emptyValue}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5 }}>
        {loading && (
          <Box sx={{ mb: 1.5 }}>
            <LinearProgress />
          </Box>
        )}
        {error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.25,
            mb: 1.5,
          }}
        >
          <DetailField label="Codigo / numero" value={code} strong />
          <DetailField label="Proveedor" value={provider} />
          <DetailField label="Fecha recepcion" value={receptionDate ? formatDate(receptionDate) : ''} />
          <DetailField label="Fecha emision" value={issueDate ? formatDate(issueDate) : ''} />
          <DetailField label="Total" value={total != null ? formatCurrency(total) : ''} strong />
          <DetailField label="Saldo" value={balance != null ? formatCurrency(balance) : ''} strong />
          <DetailField label="Estado" value={status} />
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Codigo</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell>Formato</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Subtotal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      Items no disponibles.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : items.map((item, index) => {
                const product = item.product || {};
                const quantity = item.quantity ?? item.quantityRe ?? item.qty;
                const price = item.price ?? item.finalPrice ?? item.unit_price;
                const subtotal = item.subtotal ?? (toNumber(quantity) * toNumber(price));
                return (
                  <TableRow key={item.id || item.clientRowId || index}>
                    <TableCell>{item.code || product.code || emptyValue}</TableCell>
                    <TableCell>{item.description || product.description || item.name || emptyValue}</TableCell>
                    <TableCell align="right">{quantity ?? emptyValue}</TableCell>
                    <TableCell>{item.prformat?.name || item.format_name || item.prformat || emptyValue}</TableCell>
                    <TableCell align="right">{price != null ? formatCurrency(price) : emptyValue}</TableCell>
                    <TableCell align="right">{subtotal != null ? formatCurrency(subtotal) : emptyValue}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 1.5 }} />
        <DetailField
          label="Notas / datos adicionales"
          value={Array.isArray(notes) ? notes.filter(Boolean).join('\n') : notes}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreditNoteDialog({ note, onClose }) {
  const open = Boolean(note);
  if (!open) return null;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ py: 1.25, pr: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
            Nota de credito {note.credit_number || note.number || note.credit_note_id || note.id || ''}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25}>
          <DetailField label="Numero" value={note.credit_number || note.number || note.credit_note_id} strong />
          <DetailField label="Fecha" value={formatDate(note.credit_date || note.date)} />
          <DetailField label="Monto aplicado" value={formatCurrency(note.amount_applied ?? note.amount ?? note.credit_amount)} strong />
          <DetailField label="Factura" value={[note.invoice_number, note.invoice_series].filter(Boolean).join(' - ')} />
          <DetailField label="Motivo / observacion" value={note.reason || note.comment || note.observation} />
          <DetailField label="Estado" value={note.status || (note.for_adjustments_only ? 'Solo ajustes' : 'Aplicada')} />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function CreditNotesSection({ credits = [], onViewCreditNote }) {
  const list = Array.isArray(credits) ? credits : [];
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Notas de credito
      </Typography>
      {list.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            py: 1.25,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Sin notas de credito vinculadas
          </Typography>
        </Box>
      ) : (
        <Stack spacing={0.75}>
          {list.map((note) => {
            const number = note.credit_number || note.number || note.code || note.credit_note_id || note.id;
            const amount = note.amount_applied ?? note.amount ?? note.credit_amount;
            const status = note.status || (note.for_adjustments_only ? 'Solo ajustes' : 'Aplicada');
            return (
              <Box
                key={note.id || `${number}-${amount}`}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                        {number || 'Nota'}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={status}
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[formatDate(note.credit_date || note.date), amount != null ? formatCurrency(amount) : null].filter(Boolean).join(' - ')}
                    </Typography>
                    {(note.reason || note.comment || note.observation) && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {note.reason || note.comment || note.observation}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility fontSize="small" />}
                    onClick={() => onViewCreditNote(note)}
                    sx={{ ...BTN_SX, flexShrink: 0 }}
                  >
                    Ver
                  </Button>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

function ReceiptFormDialog({ open, payment, onClose, onSaved }) {
  const [form, setForm] = useState({
    number: '',
    date: todayInputValue(),
    amount: '',
    receipt_type: 'electronico',
    is_physical: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      number: '',
      date: todayInputValue(),
      amount: String(payment?.totalAmount || ''),
      receipt_type: 'electronico',
      is_physical: false,
    });
    setError('');
  }, [open, payment?.totalAmount]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async () => {
    const paymentId = payment?.paymentIds?.[0];
    if (!paymentId) {
      setError('No se encontro el pago base para adjuntar el recibo.');
      return;
    }
    if (!String(form.number || '').trim()) {
      setError('Ingresa el numero de recibo.');
      return;
    }
    if (!(Number(form.amount) > 0)) {
      setError('El monto debe ser mayor a 0.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await SettlementsAPI.addReceipt(paymentId, {
        number: String(form.number || '').trim(),
        date: form.date,
        amount: Number(form.amount || 0),
        receipt_type: form.receipt_type || null,
        is_physical: Boolean(form.is_physical),
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo adjuntar el recibo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600, py: 1.25 }}>
        Adjuntar recibo del proveedor
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField name="number" label="Numero de recibo" size="small" value={form.number} onChange={handleChange} fullWidth />
          <TextField name="date" label="Fecha" type="date" size="small" value={form.date} onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField name="amount" label="Monto" type="number" size="small" value={form.amount} onChange={handleChange} inputProps={{ step: '0.01' }} fullWidth />
          <TextField name="receipt_type" label="Tipo" size="small" value={form.receipt_type} onChange={handleChange} fullWidth />
          <FormControlLabel
            control={<Checkbox name="is_physical" checked={Boolean(form.is_physical)} onChange={handleChange} size="small" />}
            label={<Typography variant="body2">Recibo fisico</Typography>}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.25 }}>
        <Button onClick={onClose} disabled={saving} size="small" sx={BTN_SX}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving} size="small" sx={BTN_SX}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PaymentDetailView() {
  const { id } = useParams();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const seed = location.state?.payment || null;

  const paymentIds = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const ids = (params.get('ids') || '').split(',').map((value) => value.trim()).filter(Boolean);
    const stateIds = Array.isArray(location.state?.paymentIds) ? location.state.paymentIds : [];
    const normalizeId = (value) => {
      const text = String(value ?? '').trim();
      return text || null;
    };
    return Array.from(new Set([...ids, ...stateIds, id].map(normalizeId).filter(Boolean)));
  }, [id, location.search, location.state]);

  const [payment, setPayment] = useState(seed ? normalizePaymentDetailData([], seed) : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, doc: null, data: null, loading: false, error: '' });
  const [creditDialogNote, setCreditDialogNote] = useState(null);
  const [dateCorrection, setDateCorrection] = useState({
    open: false,
    date: '',
    reason: '',
    saving: false,
    error: '',
  });
  const [methodToEdit, setMethodToEdit] = useState(null);

  const loadDetail = useCallback(async () => {
    if (!paymentIds.length) {
      setError('No se encontro el identificador del pago.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const responses = await Promise.all(paymentIds.map((paymentId) => SettlementsAPI.paymentDetail(paymentId)));
      const details = responses.map((response) => response.data);
      setPayment(normalizePaymentDetailData(details, seed || {}));
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo cargar el detalle del pago.');
    } finally {
      setLoading(false);
    }
  }, [paymentIds, seed]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleCorrectPaymentDate = async () => {
    const nextDate = dateCorrection.date;
    if (!nextDate) {
      setDateCorrection((prev) => ({ ...prev, error: 'La fecha de pago es requerida.' }));
      return;
    }
    if (nextDate > todayInputValue()) {
      setDateCorrection((prev) => ({ ...prev, error: 'La fecha de pago no puede ser futura.' }));
      return;
    }
    if (!dateCorrection.reason.trim()) {
      setDateCorrection((prev) => ({ ...prev, error: 'El motivo de corrección es requerido.' }));
      return;
    }

    const legacyIds = (payment?.raw || [])
      .filter((detail) => detail?.date_estimated)
      .map((detail) => detail.id)
      .filter(Boolean);
    const idsToCorrect = legacyIds.length ? legacyIds : paymentIds;
    setDateCorrection((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      await Promise.all(idsToCorrect.map(
        (paymentId) => SettlementsAPI.correctPaymentDate(paymentId, nextDate, dateCorrection.reason)
      ));
      setDateCorrection({ open: false, date: '', reason: '', saving: false, error: '' });
      await loadDetail();
    } catch (err) {
      const apiError = err?.response?.data?.payment_date?.[0] || err?.response?.data?.detail;
      setDateCorrection((prev) => ({ ...prev, saving: false, error: apiError || 'No se pudo corregir la fecha.' }));
    }
  };

  const onPreviewPaymentAttachment = useCallback((attachment) => {
    setPreviewAttachment(attachment);
  }, []);

  const onDownloadPaymentAttachment = useCallback((attachment) => {
    const url = getAttachmentUrl(attachment);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const startReplace = useCallback((target) => {
    setReplaceTarget(target);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const onReplacePaymentAttachment = useCallback((attachment) => {
    startReplace({ type: 'paymentAttachment', attachment });
  }, [startReplace]);

  const onAttachPaymentAttachment = useCallback((method) => {
    const documentId = method?.raw?.document?.id || method?.raw?.payment_document?.id;
    if (!documentId) {
      window.alert('No se encontró el documento del pago.');
      return;
    }
    startReplace({ type: 'paymentDocumentNew', documentId });
  }, [startReplace]);

  const onDeletePaymentAttachment = useCallback(async (attachment) => {
    if (!attachment) return;
    if (!window.confirm('Eliminar este comprobante?')) return;
    const primaryDocId = getPrimaryPaymentDocumentId(attachment);
    try {
      if (primaryDocId) {
        await API.patch(`payment/payment-documents/${primaryDocId}/`, { paymentFile: null });
      } else {
        const attachmentId = getAttachmentRecordId(attachment);
        if (!attachmentId) {
          window.alert('No se encontro el identificador del comprobante.');
          return;
        }
        await API.delete(`payment/payment-document-attachments/${attachmentId}/`);
      }
      await loadDetail();
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo eliminar el comprobante.');
    }
  }, [loadDetail]);

  const onReplaceReceiptFile = useCallback((attachment) => {
    const receipt = attachment?.raw || attachment;
    startReplace({ type: 'receiptFile', receipt });
  }, [startReplace]);

  const onDeleteReceiptFile = useCallback(async (attachment) => {
    const receipt = attachment?.raw || attachment;
    if (!receipt?.id) return;
    if (!window.confirm('Eliminar el archivo del recibo?')) return;
    try {
      await PaymentCrud.receipts.update(receipt.id, { file: null });
      await loadDetail();
    } catch (err) {
      window.alert(err?.response?.data?.detail || 'No se pudo eliminar el archivo del recibo.');
    }
  }, [loadDetail]);

  const handleReplaceFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !replaceTarget) return;

    const formData = new FormData();
    try {
      if (replaceTarget.type === 'receiptFile') {
        if (!replaceTarget.receipt?.id) throw new Error('No se encontro el recibo.');
        formData.append('file', file);
        await API.patch(`payment/receipts/${replaceTarget.receipt.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else if (replaceTarget.type === 'paymentDocumentNew') {
        if (!replaceTarget.documentId) throw new Error('No se encontró el documento del pago.');
        formData.append('paymentFile', file);
        await API.patch(`payment/payment-documents/${replaceTarget.documentId}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const attachment = replaceTarget.attachment;
        const primaryDocId = getPrimaryPaymentDocumentId(attachment);
        if (primaryDocId) {
          formData.append('paymentFile', file);
          await API.patch(`payment/payment-documents/${primaryDocId}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } else {
          const attachmentId = getAttachmentRecordId(attachment);
          if (!attachmentId) throw new Error('No se encontro el comprobante.');
          formData.append('file', file);
          await API.patch(`payment/payment-document-attachments/${attachmentId}/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
      }
      setReplaceTarget(null);
      await loadDetail();
    } catch (err) {
      window.alert(err?.response?.data?.detail || err?.message || 'No se pudo reemplazar el archivo.');
    }
  };

  const handleDeleteReceipt = async (receipt) => {
    if (!receipt?.id) return;
    if (!window.confirm('Eliminar este recibo?')) return;
    await PaymentCrud.receipts.remove(receipt.id);
    await loadDetail();
  };

  const handleViewRelatedDocument = useCallback(async (doc) => {
    setInvoiceDialog({ open: true, doc, data: null, loading: Boolean(doc?.id), error: '' });
    if (!doc?.id) return;
    try {
      const response = await API.get(`invoice/${doc.id}/`);
      setInvoiceDialog({ open: true, doc, data: response.data, loading: false, error: '' });
    } catch (err) {
      setInvoiceDialog({
        open: true,
        doc,
        data: null,
        loading: false,
        error: err?.response?.data?.detail || 'No se pudo cargar el detalle completo de la factura.',
      });
    }
  }, []);

  const hasActivity = Boolean(payment?.createdAt)
    || Boolean(payment?.corrections?.length)
    || (payment?.receipt?.items || []).some((receipt) => receipt.createdAt || receipt.date)
    || (payment?.methods || []).some((method) => (
      method?.attachments || []
    ).some((attachment) => attachment?.raw?.createdAt || attachment?.raw?.created_at));

  const methodsTotal = useMemo(
    () => (payment?.methods || []).reduce((sum, method) => sum + toNumber(method.amount), 0),
    [payment?.methods]
  );
  const hasMethodsMismatch = payment ? Math.abs(methodsTotal - toNumber(payment.totalAmount)) > 0.01 : false;

  const headerTitle = payment?.code || (id ? `Pago #${id}` : 'Detalle de pago');
  const headerSubtitle = payment
    ? `${payment.provider?.name || 'Sin proveedor'} - ${formatDate(payment.paymentDate)}`
    : 'Pago consolidado';

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: (theme) => theme.breakpoints.values.xl,
        mx: 'auto',
        px: { xs: 0.5, sm: 1, md: 1.5 },
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.25}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <MainText
            title={headerTitle}
            subtitle={headerSubtitle}
            icon={ReceiptLong}
            width="auto"
          />

          <Stack direction="row" spacing={0.75} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center">
            {payment && (
              <>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 1,
                    py: 0.45,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(payment.totalAmount)}
                  </Typography>
                </Box>
                <PaymentStatusChip status={payment.status} />
              </>
            )}
            {payment?.dateEstimated ? (
              <Button
                startIcon={<Edit fontSize="small" />}
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => setDateCorrection({
                  open: true,
                  date: String(payment.paymentDate || '').slice(0, 10),
                  reason: '',
                  saving: false,
                  error: '',
                })}
                sx={BTN_SX}
              >
                Corregir fecha estimada
              </Button>
            ) : null}
            <Button
              startIcon={<UploadFile fontSize="small" />}
              variant="outlined"
              size="small"
              onClick={() => setReceiptOpen(true)}
              disabled={!payment}
              sx={BTN_SX}
            >
              Adjuntar recibo
            </Button>
          </Stack>
        </Stack>

        {loading && (
          <SectionCard>
            <LinearProgress sx={{ mb: 1.5 }} />
            <Stack spacing={1}>
              <Skeleton height={32} />
              <Skeleton height={96} />
              <Skeleton height={150} />
            </Stack>
          </SectionCard>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && !payment && <Alert severity="info">No hay informacion del pago.</Alert>}

        {!loading && !error && payment && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2.1fr) minmax(320px, 0.9fr)' },
              gap: 1.5,
              alignItems: 'start',
            }}
          >
            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              <SectionCard>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Resumen
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                      gap: 1.25,
                    }}
                  >
                    <DetailField label="Proveedor" value={payment.provider?.name} />
                    <Box>
                      <DetailField label="Fecha de pago" value={formatDate(payment.paymentDate)} />
                      {payment.dateEstimated ? <Chip size="small" color="warning" variant="outlined" label="Legacy · fecha estimada" sx={{ mt: 0.5 }} /> : null}
                    </Box>
                    <DetailField label="Total pagado" value={formatCurrency(payment.totalAmount)} strong />
                    <DetailField label="Documento relacionado" value={payment.relatedDocuments?.map((doc) => doc.code).join(', ')} />
                    <DetailField label="Registrado por" value={payment.createdBy} />
                    <DetailField label="Estado" value={payment.status?.label} />
                  </Box>
                  <Divider />
                  <DetailField label="Observacion general" value={payment.notes?.length ? payment.notes.join('\n') : 'Sin observacion general'} />
                </Stack>
              </SectionCard>

              <SectionCard>
                <Stack spacing={1}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Metodos de pago
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(payment.methods || []).length} metodo{(payment.methods || []).length === 1 ? '' : 's'} - total {formatCurrency(methodsTotal)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(payment.totalAmount)}
                    </Typography>
                  </Stack>
                  {hasMethodsMismatch && (
                    <Alert severity="warning">
                      La suma de metodos ({formatCurrency(methodsTotal)}) no coincide con el total del pago ({formatCurrency(payment.totalAmount)}).
                    </Alert>
                  )}
                  {(payment.methods || []).length === 0 ? (
                    <Alert severity="info">No hay metodos de pago registrados.</Alert>
                  ) : (
                    <Stack spacing={1}>
                      {(payment.methods || []).map((method) => (
                        <PaymentMethodRow
                          key={method.id || `${method.type}-${method.reference}-${method.amount}`}
                          method={method}
                          onEdit={setMethodToEdit}
                          onPreviewPaymentAttachment={onPreviewPaymentAttachment}
                          onDownloadPaymentAttachment={onDownloadPaymentAttachment}
                          onReplacePaymentAttachment={onReplacePaymentAttachment}
                          onDeletePaymentAttachment={onDeletePaymentAttachment}
                          onAttachPaymentAttachment={onAttachPaymentAttachment}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </SectionCard>
            </Stack>

            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
              <SectionCard>
                <PaymentProviderReceiptSection
                  receipt={payment.receipt}
                  onAttachReceipt={() => setReceiptOpen(true)}
                  onDeleteReceipt={handleDeleteReceipt}
                  onViewReceiptFile={onPreviewPaymentAttachment}
                  onDownloadReceiptFile={onDownloadPaymentAttachment}
                  onReplaceReceiptFile={onReplaceReceiptFile}
                  onDeleteReceiptFile={onDeleteReceiptFile}
                />
              </SectionCard>

              <SectionCard>
                <PaymentRelatedDocumentsSection
                  documents={payment.relatedDocuments}
                  onViewDocument={handleViewRelatedDocument}
                />
              </SectionCard>

              <SectionCard>
                <CreditNotesSection
                  credits={payment.credits}
                  onViewCreditNote={setCreditDialogNote}
                />
              </SectionCard>

              <SectionCard>
                <PaymentNotesSection notes={payment.notes} />
              </SectionCard>

              <SectionCard>
                <PaymentAdjustmentsSection payment={payment} />
              </SectionCard>

              {hasActivity && (
                <SectionCard>
                  <PaymentActivityTimeline payment={payment} />
                </SectionCard>
              )}
            </Stack>
          </Box>
        )}
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleReplaceFileSelected}
      />

      <ReceiptFormDialog
        open={receiptOpen}
        payment={payment}
        onClose={() => setReceiptOpen(false)}
        onSaved={loadDetail}
      />

      <AttachmentPreviewDialog
        open={Boolean(previewAttachment)}
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />

      <InvoiceDetailDialog
        open={invoiceDialog.open}
        doc={invoiceDialog.doc}
        invoiceData={invoiceDialog.data}
        loading={invoiceDialog.loading}
        error={invoiceDialog.error}
        onClose={() => setInvoiceDialog({ open: false, doc: null, data: null, loading: false, error: '' })}
      />

      <CreditNoteDialog
        note={creditDialogNote}
        onClose={() => setCreditDialogNote(null)}
      />

      <PaymentMethodEditDialog
        method={methodToEdit}
        onClose={() => setMethodToEdit(null)}
        onSaved={loadDetail}
      />

      <Dialog
        open={dateCorrection.open}
        onClose={() => !dateCorrection.saving && setDateCorrection({
          open: false,
          date: '',
          reason: '',
          saving: false,
          error: '',
        })}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Corregir fecha efectiva del pago</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Alert severity="info">La fecha actual fue estimada desde el registro legacy. Esta correccion se usara en reportes historicos.</Alert>
            <DatePick
              name="payment_date"
              label="Fecha de pago"
              value={dateCorrection.date}
              onChange={(date) => setDateCorrection((prev) => ({
                ...prev,
                date: toLocalDateInput(date),
                error: '',
              }))}
              size="small"
              allowHistoricalDates
              allowFutureDates={false}
              width="100%"
              maxWidth={500}
            />
            <TextField
              autoFocus
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Motivo de corrección"
              value={dateCorrection.reason}
              onChange={(event) => setDateCorrection((prev) => ({
                ...prev,
                reason: event.target.value,
                error: '',
              }))}
              error={Boolean(dateCorrection.error)}
              helperText={dateCorrection.error}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDateCorrection({
              open: false,
              date: '',
              reason: '',
              saving: false,
              error: '',
            })}
            disabled={dateCorrection.saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCorrectPaymentDate}
            disabled={dateCorrection.saving || !dateCorrection.date || !dateCorrection.reason.trim()}
          >
            {dateCorrection.saving ? 'Guardando...' : 'Guardar fecha'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
